import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import GlobalStats from '../components/GlobalStats';
import CryptoList from '../components/CryptoList';
import CryptoChart from '../components/CryptoChart';
import AlertsPanel from '../components/AlertsPanel';
import SearchBar from '../components/SearchBar';
import MarketAnalysis from '../components/MarketAnalysis';
import CryptoNews from '../components/CryptoNews';
import { Bell, TrendingUp } from 'lucide-react';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [globalMetrics, setGlobalMetrics] = useState(null);
  const [cryptoList, setCryptoList] = useState([]);
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchInitialData();
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      refreshData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchGlobalMetrics(),
        fetchTopCryptos()
      ]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setRefreshing(true);
      await Promise.all([
        fetchGlobalMetrics(),
        fetchTopCryptos()
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchGlobalMetrics = async () => {
    try {
      const response = await axios.get(`${API}/crypto/global`);
      if (response.data.success) {
        setGlobalMetrics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching global metrics:', error);
    }
  };

  const fetchTopCryptos = async () => {
    try {
      const response = await axios.get(`${API}/crypto/top?limit=50`);
      if (response.data.success) {
        setCryptoList(response.data.data);
        if (!selectedCrypto && response.data.data.length > 0) {
          setSelectedCrypto(response.data.data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching top cryptos:', error);
    }
  };

  const handleCryptoSelect = (crypto) => {
    setSelectedCrypto(crypto);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAFC' }} data-testid="dashboard-container">
      <Toaster />
      
      {/* Header */}
      <motion.header 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white/80 backdrop-blur-lg sticky top-0 z-50"
        style={{ borderBottom: '1px solid #E5E7EB' }}
        data-testid="dashboard-header"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-center pr-4"
              data-testid="logo"
            >
              <img 
                src="/cryptoatlas-black.svg" 
                alt="CryptoAtlas" 
                className="h-8"
                style={{ width: 'auto' }}
              />
            </motion.div>
            
            <div className="flex items-center gap-4">
              <SearchBar onSelect={handleCryptoSelect} />
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAlerts(!showAlerts)}
                className="relative p-2.5 rounded-lg transition-colors"
                style={{ 
                  backgroundColor: '#EFF6FF',
                  color: '#1D4ED8'
                }}
                data-testid="alerts-toggle"
              >
                <Bell className="w-5 h-5" />
              </motion.button>
              
              {refreshing && (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 border-2 rounded-full"
                  style={{ borderColor: '#1D4ED8', borderTopColor: 'transparent' }}
                />
              )}
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-[60vh]">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full"
              data-testid="loading-spinner"
            />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Global Stats */}
            <GlobalStats metrics={globalMetrics} />

            {/* Main Grid */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Crypto List */}
              <div className="lg:col-span-1" data-testid="crypto-list-container">
                <CryptoList 
                  cryptos={cryptoList}
                  selectedCrypto={selectedCrypto}
                  onSelect={handleCryptoSelect}
                />
              </div>

              {/* Chart */}
              <div className="lg:col-span-2" data-testid="crypto-chart-container">
                <CryptoChart crypto={selectedCrypto} />
              </div>
            </div>

            {/* Market Analysis Section */}
            <div className="mt-6" data-testid="market-analysis-section">
              <MarketAnalysis />
            </div>

            {/* Crypto News Section */}
            <div className="grid lg:grid-cols-1 gap-6 mt-6">
              <div data-testid="crypto-news-section">
                <CryptoNews />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Alerts Panel */}
      <AnimatePresence>
        {showAlerts && (
          <AlertsPanel 
            onClose={() => setShowAlerts(false)} 
            cryptoList={cryptoList}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
